import { createStitches } from '@stitches/react'
import tinycolor from 'tinycolor2'

export type { CSS } from '@stitches/react'

export const DAW_COLORS = {
  blue01: '#ADCEFF',
  blue02: '#92C2FF',
  blue03: '#70A9FF',
  blue04: '#5C9DFF',
  blue05: '#4791FF',

  green01: '#ABD8B3',
  green02: '#9DD2A7',
  green03: '#81C58E',
  green04: '#73BF81',
  green05: '#65B875',

  lime01: '#D0E1A3',
  lime02: '#C9DC93',
  lime03: '#C1D784',
  lime04: '#B9D274',
  lime05: '#B1CD65',

  gold01: '#F0D07F',
  gold02: '#EEC96D',
  gold03: '#ECC35B',
  gold04: '#EABC48',
  gold05: '#E7B536',

  orange01: '#F5BD8E',
  orange02: '#F4B27C',
  orange03: '#F2A769',
  orange04: '#F09B56',
  orange05: '#EF9043',

  crimson01: '#FB9B88',
  crimson02: '#FA8B75',
  crimson03: '#F97B62',
  crimson04: '#F96A4E',
  crimson05: '#F85A3A',

  pink01: '#F88CAA',
  pink02: '#F7789C',
  pink03: '#F6658E',
  pink04: '#F55180',
  pink05: '#F43E72',

  purple01: '#EE95DA',
  purple02: '#EB84D5',
  purple03: '#E873CF',
  purple04: '#E561C9',
  purple05: '#E250C3',

  violet01: '#D1A7F1',
  violet02: '#C895EE',
  violet03: '#BF83EC',
  violet04: '#B572E9',
  violet05: '#AC60E6',

  indigo01: '#9FA8F9',
  indigo02: '#8C97F8',
  indigo03: '#7985F6',
  indigo04: '#6674F5',
  indigo05: '#5263F4'
}

export const NEW_DAW_COLORS = {
  indigo: '#B8D0FE',
  blue: '#B6E4FE',
  teal: '#AAE9E6',
  green: '#BDEABB',
  yellow: '#F6E6AB',
  orange: '#FECDB8',
  red: '#FEB8B8',
  magenta: '#F4B8FE',
  purple: '#D7B8FE',
  violet: '#B8BBFE'
}

export const LOOP_COLORS = Object.values(NEW_DAW_COLORS)

export const GRAY_COLORS = {
  /** a.k.a. white */
  gray01: '#FFFFFF',
  gray005: '#F8F8F8',
  gray02: '#F3F3F3',
  gray03: '#EBEBEB',
  gray04: '#E0E0E0',
  gray05: '#CCCCCC',
  gray06: '#C2C2C2',
  gray07: '#A3A3A3',
  gray08: '#8F8F8F',
  gray09: '#707070',
  gray10: '#5C5C5C',
  gray11: '#474747',
  gray12: '#3E3E3E',
  gray13: '#333333',
  gray14: '#292929',
  /** a.k.a. almost black */
  gray15: '#141414',
  /** a.k.a. black */
  black: '#000000'
}

/** merging colors */
const THEME_COLORS = { ...GRAY_COLORS, ...DAW_COLORS }

/** simple function that takes key of the color and optional opacity param and provides a valid HEX color */
export const getThemeColor = (color: keyof typeof THEME_COLORS, opacity = 1) =>
  opacity !== 1
    ? tinycolor(THEME_COLORS[color]).setAlpha(opacity).toHex8String()
    : THEME_COLORS[color]

/** default spacing tokens */
export const SPACE = {
  4: '4px',
  8: '8px',
  10: '10px',
  16: '16px',
  24: '24px',
  32: '32px',
  40: '40px',
  48: '48px',
  56: '56px',
  64: '64px',
  72: '72px',
  80: '80px'
}

/** change this const if font ever changes */
const DM = 'DM Sans, -apple-system, BlinkMacSystemFont, sans-serif'
const Poppins = 'Poppins, -apple-system, BlinkMacSystemFont, sans-serif'
const Mono = 'DM Mono, monospace'
const Azeret = 'Azeret Mono'

const FONT_WEIGHTS = {
  light: '300',
  regular: '400',
  medium: '500',
  semibold: '600',
  bold: '700'
}

const FONT_SIZES = {
  10: '10px',
  12: '12px',
  14: '14px',
  16: '16px',
  20: '20px',
  24: '24px',
  32: '32px'
}

const LINE_HEIGHTS = {
  14: '14px',
  16: '16px',
  18: '18px',
  20: '20px',
  24: '24px',
  25: '25px',
  32: '32px',
  40: '40px'
}

export const BORDER_RADIUS = {
  round: '9999px',
  4: '4px',
  6: '6px',
  8: '8px',
  10: '10px',
  12: '12px',
  16: '16px'
}
const SHADOWS = {
  small: `0px 2px 10px -4px ${tinycolor(GRAY_COLORS.black).setAlpha(0.15).toHex8String()}`,
  big: `0px 4px 32px 0px ${tinycolor(GRAY_COLORS.black).setAlpha(0.1).toHex8String()}`
}

export const BREAKPOINTS = {
  default: 0,
  mobile: 768,
  tablet: 1032
}

export const { styled, css, globalCss, keyframes, theme, createTheme, getCssText, config } =
  createStitches({
    media: {
      mobile: '(min-width: 768px)' as any,
      tablet: '(min-width: 1032px)' as any
    } as {}, // important config change, ingore type of media keys to stop freezes of intellisense
    theme: {
      colors: {
        ...THEME_COLORS,
        background__primary: GRAY_COLORS.gray01,
        background__secondary: GRAY_COLORS.gray02,
        background__tertiary: GRAY_COLORS.gray03,

        background_inverted__primary: GRAY_COLORS.gray15,
        background_inverted__secondary: GRAY_COLORS.gray13,
        background_inverted__tertiary: GRAY_COLORS.gray11,

        text__primary: GRAY_COLORS.gray15,
        text__secondary: 'rgba(0,0,0,.6)',
        text__tertiary: 'rgba(0,0,0,.4)',

        text_inverted__primary: 'rgb(255,255,255)',
        text_inverted__secondary: 'rgba(255,255,255,.6)',
        text_inverted__tertiary: 'rgba(255,255,255,.4)',

        button__primary: 'rgba(0,0,0,.05)',
        button__primary__hover: 'rgba(0,0,0,.1)',
        button__primary__active: 'rgba(0,0,0,.15)',

        button__secondary: 'rgba(0,0,0,0)',
        button__secondary__hover: 'rgba(0,0,0,.05)',
        button__secondary__active: 'rgba(0,0,0,.1)',

        button__filled: GRAY_COLORS.gray15,
        button__filled__hover: 'rgba(0,0,0,.6)',
        button__filled__active: 'rgba(0,0,0,.9)',
        button__success_text: DAW_COLORS.blue05,
        button__success: tinycolor(DAW_COLORS.blue04).setAlpha(0.2).toHex8String(),
        button__success__hover: tinycolor(DAW_COLORS.blue04).setAlpha(0.4).toHex8String(),
        button__success__active: tinycolor(DAW_COLORS.blue04).setAlpha(0.6).toHex8String(),

        button__border: 'rgba(0,0,0,.5)'
      },
      space: SPACE,
      fonts: {
        dm: DM,
        poppins: Poppins,
        mono: Mono,
        azeret: Azeret
      },
      fontSizes: FONT_SIZES,
      fontWeights: FONT_WEIGHTS,
      lineHeights: LINE_HEIGHTS,
      radii: BORDER_RADIUS,
      shadows: SHADOWS
    }
  })
